// src/services/upload.service.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Upload Service - Handles file uploads to Firebase Storage via backend API
 */
class UploadService {
    /**
     * Check if Firebase Storage is configured and available
     * @returns {Promise<Object>} - { firebaseConfigured, message }
     */
    async checkStatus() {
        try {
            const response = await axios.get(`${API_URL}/api/upload/status`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('❌ Check upload status error:', error);
            throw error;
        }
    }

    /**
     * Upload single file
     * @param {File} file - File object from input
     * @param {String} folder - Storage folder (e.g., 'documents', 'screenshots', 'profile-pictures')
     * @param {Object} metadata - Additional metadata
     * @returns {Promise<Object>} - Upload result with downloadURL, filePath, etc.
     */
    async uploadSingle(file, folder = 'uploads', metadata = {}) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', folder);

            // Add metadata fields
            Object.keys(metadata).forEach(key => {
                formData.append(key, metadata[key]);
            });

            const response = await axios.post(
                `${API_URL}/api/upload/single`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: metadata.onProgress
                }
            );

            return response.data;
        } catch (error) {
            console.error('❌ Single file upload error:', error);
            throw error;
        }
    }

    /**
     * Upload multiple files
     * @param {Array<File>} files - Array of File objects
     * @param {String} folder - Storage folder
     * @param {Object} metadata - Additional metadata
     * @returns {Promise<Object>} - Upload results for all files
     */
    async uploadMultiple(files, folder = 'uploads', metadata = {}) {
        try {
            const formData = new FormData();

            files.forEach(file => {
                formData.append('files', file);
            });

            formData.append('folder', folder);

            // Add metadata fields
            Object.keys(metadata).forEach(key => {
                formData.append(key, metadata[key]);
            });

            const response = await axios.post(
                `${API_URL}/api/upload/multiple`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: metadata.onProgress
                }
            );

            return response.data;
        } catch (error) {
            console.error('❌ Multiple file upload error:', error);
            throw error;
        }
    }

    /**
     * Delete single file
     * @param {String} filePath - Path to file in storage
     * @returns {Promise<Object>}
     */
    async deleteFile(filePath) {
        try {
            const response = await axios.delete(`${API_URL}/api/upload`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                data: { filePath }
            });
            return response.data;
        } catch (error) {
            console.error('❌ Delete file error:', error);
            throw error;
        }
    }

    /**
     * Delete multiple files
     * @param {Array<String>} filePaths - Array of file paths
     * @returns {Promise<Object>}
     */
    async deleteMultipleFiles(filePaths) {
        try {
            const response = await axios.delete(`${API_URL}/api/upload/multiple`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                data: { filePaths }
            });
            return response.data;
        } catch (error) {
            console.error('❌ Delete multiple files error:', error);
            throw error;
        }
    }

    /**
     * Get signed URL for file
     * @param {String} filePath - Path to file in storage
     * @param {Number} expiryDays - URL expiry in days (default: 7)
     * @returns {Promise<Object>} - { url, expiresIn }
     */
    async getSignedUrl(filePath, expiryDays = 7) {
        try {
            const response = await axios.post(
                `${API_URL}/api/upload/signed-url`,
                { filePath, expiryDays },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('❌ Get signed URL error:', error);
            throw error;
        }
    }

    /**
     * Check if file exists
     * @param {String} filePath - Path to file in storage
     * @returns {Promise<Boolean>}
     */
    async fileExists(filePath) {
        try {
            const response = await axios.post(
                `${API_URL}/api/upload/exists`,
                { filePath },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                }
            );
            return response.data.data.exists;
        } catch (error) {
            console.error('❌ Check file exists error:', error);
            return false;
        }
    }

    /**
     * Get file metadata
     * @param {String} filePath - Path to file in storage
     * @returns {Promise<Object>} - File metadata
     */
    async getFileMetadata(filePath) {
        try {
            const response = await axios.post(
                `${API_URL}/api/upload/metadata`,
                { filePath },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('❌ Get file metadata error:', error);
            throw error;
        }
    }

    /**
     * Helper: Upload profile picture
     * @param {File} file - Image file
     * @returns {Promise<Object>}
     */
    async uploadProfilePicture(file) {
        return this.uploadSingle(file, 'profile-pictures', {
            category: 'profile'
        });
    }

    /**
     * Helper: Upload document
     * @param {File} file - Document file
     * @param {String} documentType - Type of document (e.g., 'id-proof', 'address-proof')
     * @returns {Promise<Object>}
     */
    async uploadDocument(file, documentType) {
        return this.uploadSingle(file, 'documents', {
            documentType,
            category: 'document'
        });
    }

    /**
     * Helper: Upload screenshot
     * @param {File} file - Screenshot file
     * @returns {Promise<Object>}
     */
    async uploadScreenshot(file) {
        return this.uploadSingle(file, 'screenshots', {
            category: 'screenshot'
        });
    }
}

export default new UploadService();
